import { languages } from '../data/generalData';

// Maps our app locales to Google Translate's language codes, derived from
// `languages` so adding a language there is enough to wire it up here too.
const GOOGLE_TRANSLATE_LOCALE_MAP = languages.reduce((map, lang) => {
    map[lang.locale] = lang.googleCode || lang.locale;
    return map;
}, {});

const GOOGLE_TRANSLATE_LANGUAGES = Object.values(GOOGLE_TRANSLATE_LOCALE_MAP).join(',');

let scriptRequested = false;

const READY_POLL_INTERVAL_MS = 200;
const READY_POLL_TIMEOUT_MS = 8000;

let pendingPoll = null;

function setGoogleTranslateCookie(googleLangCode) {
    const value = `/en/${googleLangCode}`;

    document.cookie = `googtrans=${value};path=/`;
    document.cookie = `googtrans=${value};path=/;domain=${window.location.hostname}`;
}

// How many times to force-redispatch the change event once the <select> is
// found: Google's own handler can still ignore the very first one or two
// events while it's finishing internal setup, even though the element
// already exists in the DOM.
const FORCE_DISPATCH_ATTEMPTS = 6;

function dispatchSelectChange(googleLangCode) {
    const select = document.querySelector('#google_translate_element select.goog-te-combo');

    if (!select) {
        return false;
    }

    select.value = googleLangCode;
    select.dispatchEvent(new Event('change'));

    return true;
}

/**
 * Switches the page translation to the given app locale.
 * The Google widget mounts asynchronously after its script loads (timing
 * varies with network/cache), and even once mounted its first change event
 * can be silently ignored, so this keeps redispatching for a short window
 * instead of giving up after a single attempt.
 */
export function setGoogleTranslateLocale(locale) {
    const googleLangCode = GOOGLE_TRANSLATE_LOCALE_MAP[locale] || 'en';

    setGoogleTranslateCookie(googleLangCode);

    if (pendingPoll) {
        clearInterval(pendingPoll);
        pendingPoll = null;
    }

    let dispatchCount = 0;
    const deadline = Date.now() + READY_POLL_TIMEOUT_MS;

    const attempt = () => {
        if (dispatchSelectChange(googleLangCode)) {
            dispatchCount += 1;
        }

        if (dispatchCount >= FORCE_DISPATCH_ATTEMPTS || Date.now() > deadline) {
            clearInterval(pendingPoll);
            pendingPoll = null;
        }
    };

    attempt();

    if (pendingPoll === null && dispatchCount < FORCE_DISPATCH_ATTEMPTS) {
        pendingPoll = setInterval(attempt, READY_POLL_INTERVAL_MS);
    }
}

/**
 * Google Translate rewrites text nodes in place. If React later tries to
 * update or remove one of those same nodes (e.g. async data replacing
 * placeholder text), the DOM no longer matches what React expects and
 * `removeChild`/`insertBefore` throw, crashing the whole app. This is a
 * well-known conflict (see facebook/react#11538); patching these two DOM
 * methods to no-op on a mismatch instead of throwing is the standard fix.
 */
function patchReactGoogleTranslateConflict() {
    if (typeof Node !== 'function' || !Node.prototype) {
        return;
    }

    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function removeChild(child) {
        if (child.parentNode !== this) {
            return child;
        }
        return originalRemoveChild.call(this, child);
    };

    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function insertBefore(newNode, referenceNode) {
        if (referenceNode && referenceNode.parentNode !== this) {
            return newNode;
        }
        return originalInsertBefore.call(this, newNode, referenceNode);
    };
}

/** Loads the Google Translate Element script and mounts the (hidden) widget once. */
export function initGoogleTranslate() {
    if (scriptRequested) {
        return;
    }
    scriptRequested = true;

    patchReactGoogleTranslateConflict();

    window.googleTranslateElementInit = () => {
        // eslint-disable-next-line no-new
        new window.google.translate.TranslateElement(
            {
                pageLanguage: 'en',
                includedLanguages: GOOGLE_TRANSLATE_LANGUAGES,
                autoDisplay: false,
            },
            'google_translate_element',
        );
    };

    const script = document.createElement('script');
    script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    document.body.appendChild(script);
}

/** Reads the currently applied Google Translate locale from its cookie, defaulting to 'en'. */
export function getGoogleTranslateLocaleFromCookie() {
    const match = document.cookie.match(/googtrans=\/en\/([a-zA-Z-]+)/);

    if (!match) {
        return 'en';
    }

    const entry = Object.entries(GOOGLE_TRANSLATE_LOCALE_MAP).find(([, code]) => code === match[1]);

    return entry ? entry[0] : 'en';
}
